import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, TestTube2, Users } from 'lucide-react';
import { AppPicker, useApps } from '../../components/AppPicker';
import { UserFilters } from '../../components/UserFilters';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  cn,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  AppKey,
  Campaign,
  CustomMessage,
  PushSendResult,
  UserFilter,
} from '../../lib/types';
import { TemplateLink, useTemplates } from './Templates';

/** How long the audience count waits for typing to settle. */
const COUNT_DEBOUNCE_MS = 400;

/** Where the copy comes from: a saved template, or typed for this send. */
type Source = 'template' | 'custom';

export function ComposeTab({ onSent }: { onSent: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const apps = useApps();
  const templates = useTemplates();

  const [appKey, setAppKey] = useState<AppKey>();
  const [source, setSource] = useState<Source>('template');
  const [templateId, setTemplateId] = useState<number>();
  const [custom, setCustom] = useState({ title: '', body: '', link: '' });
  const [filter, setFilter] = useState<UserFilter>({});
  const [testDeviceId, setTestDeviceId] = useState('');

  const audience = useMemo<UserFilter>(
    () => compact({ ...filter, app_key: appKey }),
    [filter, appKey],
  );
  const debouncedAudience = useDebounced(audience, COUNT_DEBOUNCE_MS);

  const count = useQuery({
    queryKey: ['audience-count', debouncedAudience],
    queryFn: () =>
      api.post<{ count: number }>(
        '/api/notifications/audience-count',
        debouncedAudience,
      ),
    enabled: !!appKey,
  });

  const template = templates.data?.find((item) => item.id === templateId);

  const message: CustomMessage | undefined =
    custom.title.trim() && custom.body.trim()
      ? compact({
          title: custom.title.trim(),
          body: custom.body.trim(),
          link: custom.link.trim(),
        }) as CustomMessage
      : undefined;

  /** Exactly one of the two, as the API requires. */
  const copy =
    source === 'template'
      ? templateId !== undefined
        ? { template_id: templateId }
        : undefined
      : message
        ? { message }
        : undefined;
  const copyLabel = source === 'template' ? template?.name : message?.title;

  const send = useMutation({
    mutationFn: () =>
      api.post<Campaign>('/api/notifications/send', {
        ...copy,
        filter: audience,
      }),
    onSuccess: (campaign) => {
      toast.success(
        `Sending to ${formatNumber(campaign.total_recipients)} device(s) — follow it in History`,
      );
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onSent();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const sendTest = useMutation({
    mutationFn: () =>
      api.post<PushSendResult>('/api/notifications/test', {
        ...copy,
        device_id: testDeviceId.trim(),
      }),
    onSuccess: (result) => {
      if (result.sent > 0) toast.success('Test notification sent');
      else if (result.skipped > 0) {
        toast.error(
          'Skipped — that device has no usable push token for this transport',
        );
      } else toast.error('The push provider rejected the test');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const selectApp = (next: AppKey) => {
    setAppKey(next);
    setFilter({});
  };

  const ready = !!appKey && !!copy && (count.data?.count ?? 0) > 0;

  return (
    <div className="space-y-4">
      <Step number={1} title="Choose the app">
        <AppPicker value={appKey} onSelect={selectApp} />
      </Step>

      {!appKey ? (
        <Card>
          <EmptyState
            icon={<Send size={28} />}
            title="Pick an app to compose a notification"
            description="Push credentials are per app, so every send targets exactly one."
          />
        </Card>
      ) : (
        <>
          <Step number={2} title="Choose the message">
            <div
              role="tablist"
              aria-label="Message source"
              className="mb-3 inline-flex rounded-lg border border-line bg-raised p-0.5"
            >
              {(
                [
                  ['template', 'Use a template'],
                  ['custom', 'Write a message'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={source === value}
                  onClick={() => setSource(value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    source === value
                      ? 'bg-surface text-ink shadow-sm'
                      : 'text-ink-2 hover:text-ink',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {source === 'custom' ? (
              <CustomMessageFields value={custom} onChange={setCustom} />
            ) : (
              <>
                {templates.isError && <ErrorNote error={templates.error} />}
                <Select
                  value={templateId ?? ''}
                  onChange={(event) =>
                    setTemplateId(
                      event.target.value ? Number(event.target.value) : undefined,
                    )
                  }
                  aria-label="Template"
                >
                  <option value="">Select a template…</option>
                  {templates.data?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>

                {template && (
                  <div className="mt-3 rounded-lg border border-line bg-raised px-3 py-2.5">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <Badge tone="brand">
                        {template.locales.length} language
                        {template.locales.length === 1 ? '' : 's'}
                      </Badge>
                      <span className="font-mono text-xs text-ink-3">
                        {template.key}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-ink">
                      {englishTitle(template.locales)}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-2">
                      {englishBody(template.locales)}
                    </p>
                    {template.link && (
                      <div className="mt-2">
                        <TemplateLink link={template.link} />
                      </div>
                    )}
                    <p className="mt-2 text-xs text-ink-3">
                      Each device receives this in its own language where a
                      translation exists, English otherwise. It also stays in
                      the app's notification inbox.
                    </p>
                  </div>
                )}
              </>
            )}
          </Step>

          <Step number={3} title="Choose the audience">
            <UserFilters
              value={filter}
              onChange={setFilter}
              apps={apps.data?.find((app) => app.app_key === appKey)}
            />

            <div className="mt-3 flex items-center gap-2 rounded-lg border border-line bg-surface px-3.5 py-3">
              <Users size={16} className="text-ink-3" />
              {count.isPending || count.isFetching ? (
                <Spinner className="size-4" />
              ) : count.isError ? (
                <span className="text-sm text-danger">
                  {errorMessage(count.error)}
                </span>
              ) : (
                <span className="text-sm text-ink">
                  <span className="tnum font-semibold">
                    {formatNumber(count.data?.count ?? 0)}
                  </span>{' '}
                  device{count.data?.count === 1 ? '' : 's'} will receive this
                </span>
              )}
            </div>
          </Step>

          <Step number={4} title="Test, then send">
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <Field
                  label="Test device id"
                  hint="Delivers to one device only"
                  className="min-w-[220px] flex-1"
                >
                  <Input
                    value={testDeviceId}
                    onChange={(event) => setTestDeviceId(event.target.value)}
                    placeholder="Paste a device id"
                  />
                </Field>
                <Button
                  icon={<TestTube2 size={16} />}
                  disabled={!copy || !testDeviceId.trim()}
                  loading={sendTest.isPending}
                  onClick={() => sendTest.mutate()}
                >
                  Send test
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
                <p className="text-xs text-ink-3">
                  Sending runs in the background; progress appears under History.
                </p>
                <Button
                  variant="primary"
                  icon={<Send size={16} />}
                  disabled={!ready}
                  loading={send.isPending}
                  onClick={() => {
                    const total = count.data?.count ?? 0;
                    if (
                      confirm(
                        `Send “${copyLabel}” to ${formatNumber(total)} device(s)?`,
                      )
                    ) {
                      send.mutate();
                    }
                  }}
                >
                  Send notification
                </Button>
              </div>
            </div>
          </Step>
        </>
      )}
    </div>
  );
}

/**
 * Copy typed for this send only. One language: every recipient gets these
 * exact words, so it suits a message you won't send twice — anything reusable
 * or worth translating belongs in a template.
 */
function CustomMessageFields({
  value,
  onChange,
}: {
  value: { title: string; body: string; link: string };
  onChange: (next: { title: string; body: string; link: string }) => void;
}) {
  return (
    <div className="space-y-3">
      <Field label="Title">
        <Input
          value={value.title}
          onChange={(event) => onChange({ ...value, title: event.target.value })}
          maxLength={255}
          placeholder="Notification title"
        />
      </Field>
      <Field label="Message">
        <Textarea
          value={value.body}
          onChange={(event) => onChange({ ...value, body: event.target.value })}
          maxLength={1000}
          rows={3}
          placeholder="What do you want to say?"
        />
      </Field>
      <Field label="Link" hint="Optional — opened when the notification is tapped">
        <Input
          type="url"
          value={value.link}
          onChange={(event) => onChange({ ...value, link: event.target.value })}
          maxLength={1024}
          placeholder="https://"
        />
      </Field>
      <p className="text-xs text-ink-3">
        Sent exactly as written to every device, whatever its language, and
        kept in the app's notification inbox.
      </p>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={title}
        action={
          <span className="grid h-6 w-6 place-items-center rounded-full bg-raised text-xs font-semibold text-ink-2">
            {number}
          </span>
        }
      />
      <div className="p-4 sm:p-5">{children}</div>
    </Card>
  );
}

function englishTitle(locales: { language: string; title: string }[]): string {
  return (
    locales.find((locale) => locale.language === 'en')?.title ??
    locales[0]?.title ??
    ''
  );
}

function englishBody(locales: { language: string; body: string }[]): string {
  return (
    locales.find((locale) => locale.language === 'en')?.body ??
    locales[0]?.body ??
    ''
  );
}

/**
 * Delays a value until it stops changing, so dragging a date picker or typing
 * a language code doesn't fire a count query per keystroke.
 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
